import { useState, useEffect, useCallback } from 'react';
import type { Article, FAQ } from '../types';
import { getApiUrl } from '../config/api';

// ─── helpers ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'guides', label: 'Guides' },
  { value: 'costs', label: 'Costs & Pricing' },
  { value: 'problems', label: 'Troubleshooting' },
  { value: 'comparisons', label: 'Comparisons' },
  { value: 'reviews', label: 'Reviews' },
];

const BRANDS = [
  { value: '', label: 'All Brands (shared)' },
  { value: 'copierfinder', label: 'Copier Finder' },
  { value: 'printdealfinder', label: 'Print Deal Finder' },
  { value: 'officeprinters', label: 'Office Printers UK' },
  { value: 'lingscopiers', label: "Ling's Copiers" },
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

// ─── FAQ builder ─────────────────────────────────────────────────────────────

interface FaqBuilderProps {
  articleSlug: string;
  brandId: string | null;
}

interface FaqDraft {
  id?: number;
  question: string;
  answer: string;
  isDirty?: boolean;
}

function FaqBuilder({ articleSlug, brandId }: FaqBuilderProps) {
  const [faqs, setFaqs] = useState<FaqDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null); // index being saved
  const [newQ, setNewQ] = useState('');
  const [newA, setNewA] = useState('');
  const [adding, setAdding] = useState(false);

  const faqPage = `blog/${articleSlug}`;

  const loadFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: faqPage });
      if (brandId) params.set('brand', brandId);
      const res = await fetch(getApiUrl(`/api/faqs/admin?${params}`), {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setFaqs((data.faqs || []).map((f: FAQ) => ({ id: f.id, question: f.question, answer: f.answer })));
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [faqPage, brandId]);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  const handleSaveFaq = async (index: number) => {
    const faq = faqs[index];
    if (!faq.question.trim() || !faq.answer.trim()) return;
    setSaving(index);
    try {
      if (faq.id) {
        await fetch(getApiUrl(`/api/faqs/${faq.id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ question: faq.question, answer: faq.answer }),
        });
      }
      setFaqs((prev) => prev.map((f, i) => (i === index ? { ...f, isDirty: false } : f)));
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteFaq = async (index: number) => {
    const faq = faqs[index];
    if (!window.confirm('Delete this FAQ?')) return;
    if (faq.id) {
      await fetch(getApiUrl(`/api/faqs/${faq.id}`), {
        method: 'DELETE',
        credentials: 'include',
      });
    }
    setFaqs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddFaq = async () => {
    if (!newQ.trim() || !newA.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(getApiUrl('/api/faqs'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          brandId: brandId || null,
          question: newQ,
          answer: newA,
          page: faqPage,
          sort_order: faqs.length,
          active: 1,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setFaqs((prev) => [...prev, { id: data.faqId, question: newQ, answer: newA }]);
        setNewQ('');
        setNewA('');
      }
    } finally {
      setAdding(false);
    }
  };

  const updateFaq = (index: number, field: 'question' | 'answer', value: string) => {
    setFaqs((prev) =>
      prev.map((f, i) => (i === index ? { ...f, [field]: value, isDirty: true } : f))
    );
  };

  if (loading) {
    return <p className="text-dark-text-secondary text-sm">Loading FAQs...</p>;
  }

  return (
    <div className="space-y-4">
      {/* Existing FAQs */}
      {faqs.length === 0 && (
        <p className="text-dark-text-secondary text-sm italic">
          No FAQs yet. Add some below — they'll appear at the bottom of this article and
          generate FAQ schema for Google.
        </p>
      )}

      {faqs.map((faq, index) => (
        <div key={faq.id ?? index} className="bg-dark-bg rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-1">
              Question
            </label>
            <input
              type="text"
              value={faq.question}
              onChange={(e) => updateFaq(index, 'question', e.target.value)}
              className="w-full px-3 py-2 bg-dark-surface border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-1">
              Answer
            </label>
            <textarea
              value={faq.answer}
              onChange={(e) => updateFaq(index, 'answer', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-dark-surface border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500 resize-y"
            />
          </div>
          <div className="flex justify-end gap-2">
            {faq.isDirty && (
              <button
                onClick={() => handleSaveFaq(index)}
                disabled={saving === index}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition disabled:opacity-50"
              >
                {saving === index ? 'Saving…' : 'Save'}
              </button>
            )}
            <button
              onClick={() => handleDeleteFaq(index)}
              className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-xs font-medium rounded-lg transition"
            >
              Delete
            </button>
          </div>
        </div>
      ))}

      {/* Add new FAQ */}
      <div className="border border-dashed border-dark-border rounded-lg p-4 space-y-3">
        <p className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wide">
          Add FAQ
        </p>
        <input
          type="text"
          placeholder="Question"
          value={newQ}
          onChange={(e) => setNewQ(e.target.value)}
          className="w-full px-3 py-2 bg-dark-surface border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
        />
        <textarea
          placeholder="Answer"
          value={newA}
          onChange={(e) => setNewA(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 bg-dark-surface border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500 resize-y"
        />
        <button
          onClick={handleAddFaq}
          disabled={adding || !newQ.trim() || !newA.trim()}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50"
        >
          {adding ? 'Adding…' : '+ Add FAQ'}
        </button>
      </div>
    </div>
  );
}

// ─── Article form ─────────────────────────────────────────────────────────────

interface ArticleFormState {
  brandId: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  featuredImage: string;
  metaTitle: string;
  metaDescription: string;
  published: boolean;
}

const emptyForm: ArticleFormState = {
  brandId: '',
  slug: '',
  title: '',
  excerpt: '',
  content: '',
  category: 'guides',
  featuredImage: '',
  metaTitle: '',
  metaDescription: '',
  published: false,
};

interface ArticlePanelProps {
  article: Article | null; // null = creating new
  onSaved: () => void;
  onClose: () => void;
}

function ArticlePanel({ article, onSaved, onClose }: ArticlePanelProps) {
  const isNew = article === null;
  const [form, setForm] = useState<ArticleFormState>(
    article
      ? {
          brandId: article.brand_id ?? '',
          slug: article.slug,
          title: article.title,
          excerpt: article.excerpt ?? '',
          content: article.content,
          category: article.category,
          featuredImage: article.featured_image ?? '',
          metaTitle: article.meta_title ?? '',
          metaDescription: article.meta_description ?? '',
          published: article.published === 1,
        }
      : { ...emptyForm }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [slugManual, setSlugManual] = useState(!isNew);

  const set = (field: keyof ArticleFormState, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleTitleChange = (value: string) => {
    set('title', value);
    if (!slugManual) {
      set('slug', slugify(value));
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim() || !form.content.trim()) {
      setError('Title, slug, and content are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = {
        brandId: form.brandId || null,
        slug: form.slug,
        title: form.title,
        excerpt: form.excerpt || null,
        content: form.content,
        category: form.category,
        featuredImage: form.featuredImage || null,
        metaTitle: form.metaTitle || null,
        metaDescription: form.metaDescription || null,
        published: form.published,
      };

      const url = isNew
        ? getApiUrl('/api/articles')
        : getApiUrl(`/api/articles/${article!.id}`);
      const method = isNew ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Failed to save article');
        return;
      }

      onSaved();
    } catch {
      setError('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!article) return;
    if (!window.confirm('Permanently delete this article and its FAQs?')) return;
    await fetch(getApiUrl(`/api/articles/${article.id}`), {
      method: 'DELETE',
      credentials: 'include',
    });
    onSaved();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border flex-shrink-0">
        <h2 className="text-lg font-semibold text-white">
          {isNew ? 'New Article' : 'Edit Article'}
        </h2>
        <button
          onClick={onClose}
          className="text-dark-text-secondary hover:text-white transition"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">

        {/* Brand */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">Brand</label>
          <select
            value={form.brandId}
            onChange={(e) => set('brandId', e.target.value)}
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
          >
            {BRANDS.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">
            Title <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="How to Choose the Right Copier for Your Business"
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">
            URL Slug <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={form.slug}
            onChange={(e) => {
              setSlugManual(true);
              set('slug', e.target.value);
            }}
            placeholder="how-to-choose-the-right-copier"
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text text-sm font-mono focus:outline-none focus:border-blue-500"
          />
          <p className="text-xs text-dark-text-secondary mt-1">
            /blog/?article=<span className="text-blue-400">{form.slug || 'your-slug'}</span>
          </p>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">Category</label>
          <select
            value={form.category}
            onChange={(e) => set('category', e.target.value)}
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {/* Excerpt */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">Excerpt</label>
          <textarea
            value={form.excerpt}
            onChange={(e) => set('excerpt', e.target.value)}
            rows={2}
            placeholder="A brief summary shown in the article listing…"
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500 resize-y"
          />
        </div>

        {/* Content */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">
            Content <span className="text-red-400">*</span>
          </label>
          <textarea
            value={form.content}
            onChange={(e) => set('content', e.target.value)}
            rows={14}
            placeholder="Write your article content here. HTML is supported."
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text text-sm font-mono focus:outline-none focus:border-blue-500 resize-y"
          />
          <p className="text-xs text-dark-text-secondary mt-1">HTML is supported.</p>
        </div>

        {/* Featured Image */}
        <div>
          <label className="block text-sm font-medium text-dark-text mb-1">Featured Image URL</label>
          <input
            type="url"
            value={form.featuredImage}
            onChange={(e) => set('featuredImage', e.target.value)}
            placeholder="https://..."
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* SEO */}
        <details className="group">
          <summary className="text-sm font-medium text-dark-text cursor-pointer select-none flex items-center gap-2">
            <svg
              className="w-4 h-4 text-dark-text-secondary transition-transform group-open:rotate-90"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            SEO Metadata
          </summary>
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                Meta Title
              </label>
              <input
                type="text"
                value={form.metaTitle}
                onChange={(e) => set('metaTitle', e.target.value)}
                placeholder="Defaults to article title"
                className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                Meta Description
              </label>
              <textarea
                value={form.metaDescription}
                onChange={(e) => set('metaDescription', e.target.value)}
                rows={2}
                placeholder="Defaults to excerpt"
                className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500 resize-y"
              />
            </div>
          </div>
        </details>

        {/* Published toggle */}
        <div className="flex items-center justify-between py-3 border-t border-dark-border">
          <div>
            <p className="text-sm font-medium text-dark-text">Published</p>
            <p className="text-xs text-dark-text-secondary">
              {form.published ? 'Visible on all sites' : 'Draft — not visible publicly'}
            </p>
          </div>
          <button
            onClick={() => set('published', !form.published)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.published ? 'bg-blue-600' : 'bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                form.published ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition disabled:opacity-50"
        >
          {saving ? 'Saving…' : isNew ? 'Create Article' : 'Save Changes'}
        </button>

        {!isNew && (
          <button
            onClick={handleDelete}
            className="w-full py-2 text-red-400 hover:text-red-300 text-sm transition"
          >
            Delete article
          </button>
        )}

        {/* FAQ Builder — only shown when editing an existing article with a slug */}
        {!isNew && form.slug && (
          <div className="border-t border-dark-border pt-6">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-white mb-1">FAQ Builder</h3>
              <p className="text-xs text-dark-text-secondary">
                FAQs appear at the bottom of this article and generate structured FAQ schema
                for Google Search.
              </p>
            </div>
            <FaqBuilder articleSlug={form.slug} brandId={form.brandId || null} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function BlogPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterBrand, setFilterBrand] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [selected, setSelected] = useState<Article | null | 'new'>(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      // Admin fetch — no brand filter in header so we get all
      const res = await fetch(getApiUrl('/api/articles?limit=200'), {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setArticles(data.articles || []);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const filtered = articles.filter((a) => {
    if (filterBrand && a.brand_id !== filterBrand) return false;
    if (filterStatus === 'published' && a.published !== 1) return false;
    if (filterStatus === 'draft' && a.published !== 0) return false;
    return true;
  });

  const handlePanelSaved = () => {
    setSelected(null);
    fetchArticles();
  };

  const panelOpen = selected !== null;

  return (
    <div className="flex h-full gap-0">
      {/* ── Article list ── */}
      <div
        className={`flex flex-col flex-1 min-w-0 transition-all duration-200 ${
          panelOpen ? 'hidden lg:flex' : 'flex'
        }`}
      >
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Blog &amp; Articles</h1>
            <p className="text-dark-text-secondary text-sm mt-1">
              Manage articles and their FAQ sections across all sites.
            </p>
          </div>
          <button
            onClick={() => setSelected('new')}
            className="btn btn-primary whitespace-nowrap"
          >
            + New Article
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <select
            value={filterBrand}
            onChange={(e) => setFilterBrand(e.target.value)}
            className="px-3 py-2 bg-dark-surface border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
          >
            {BRANDS.map((b) => (
              <option key={b.value} value={b.value}>
                {b.value === '' ? 'All Brands' : b.label}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
            className="px-3 py-2 bg-dark-surface border border-dark-border rounded-lg text-dark-text text-sm focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          <span className="text-dark-text-secondary text-sm self-center">
            {filtered.length} article{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Table */}
        <div className="bg-dark-surface rounded-xl border border-dark-border overflow-hidden flex-1">
          {loading ? (
            <div className="p-8 text-center text-dark-text-secondary">Loading articles…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-dark-text-secondary mb-4">No articles found.</p>
              <button onClick={() => setSelected('new')} className="btn btn-primary">
                Create your first article
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-border">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-dark-text-secondary uppercase tracking-wide">
                      Title
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-dark-text-secondary uppercase tracking-wide hidden sm:table-cell">
                      Category
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-dark-text-secondary uppercase tracking-wide hidden md:table-cell">
                      Brand
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-dark-text-secondary uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {filtered.map((article) => (
                    <tr
                      key={article.id}
                      className="hover:bg-slate-700/30 transition-colors cursor-pointer"
                      onClick={() => setSelected(article)}
                    >
                      <td className="px-6 py-4">
                        <p className="text-dark-text font-medium text-sm line-clamp-1">
                          {article.title}
                        </p>
                        <p className="text-dark-text-secondary text-xs mt-0.5 font-mono">
                          {article.slug}
                        </p>
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <span className="text-dark-text-secondary text-sm capitalize">
                          {article.category}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span className="text-dark-text-secondary text-sm">
                          {article.brand_id
                            ? BRANDS.find((b) => b.value === article.brand_id)?.label ?? article.brand_id
                            : 'All Brands'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            article.published
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-slate-500/20 text-slate-400'
                          }`}
                        >
                          {article.published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelected(article);
                          }}
                          className="text-blue-400 hover:text-blue-300 text-sm font-medium transition"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Edit / Create panel ── */}
      {panelOpen && (
        <div className="flex flex-col w-full lg:w-[520px] lg:ml-6 bg-dark-surface rounded-xl border border-dark-border overflow-hidden flex-shrink-0">
          <ArticlePanel
            article={selected === 'new' ? null : selected}
            onSaved={handlePanelSaved}
            onClose={() => setSelected(null)}
          />
        </div>
      )}
    </div>
  );
}
